import { useState, type CSSProperties } from "react";
import { getSelectedTheme } from "../../theme/themes";
import { cardFaceUrl, HOME_HAND } from "../transitions/cardImages";
import { useCardPack } from "../transitions/useCardPack";
import { fidgetClass, useCardFidget } from "./cardFidget";

/**
 * A hand of five of the player's own cards, fanned across the home screen, so
 * the first thing anybody sees is the game - in the deck they chose, painted
 * ones included - rather than a title and a list of buttons.
 *
 * It renders nothing of its own. The screen transitions already build a pack of
 * this deck's cards on idle at launch (`cardImages.ts`); this shows five of
 * them as they arrive. Until the rendered back exists the cards are drawn in the
 * deck's two colors, so the hand is never blank and costs nothing before first
 * paint; each card then turns face up as its face lands, staggered like a deal.
 * On a later visit the pack is already built and the hand is simply there.
 */
export function HomeHand() {
  // Faces that were already rendered when the screen opened are shown face up
  // from the first frame; only ones that arrive while it is open flip over.
  const [initiallyUp] = useState(() => HOME_HAND.map((i) => cardFaceUrl(i) !== null));
  const { back, face: faceOf } = useCardPack();
  const theme = getSelectedTheme();
  const last = HOME_HAND.length - 1;
  // One card at a time hops, or turns over and back, while the menu sits open.
  const { fidget, done } = useCardFidget(HOME_HAND.length);

  return (
    <div className="home-hand" aria-hidden="true">
      {HOME_HAND.map((index, i) => {
        const face = faceOf(index);
        // -2 .. 2 across the fan: the angle, and how far each card sits out.
        const k = i - last / 2;
        const style = {
          ["--k" as string]: k,
          ["--deal" as string]: `${i * 90}ms`,
          zIndex: i === last / 2 ? 5 : 5 - Math.abs(k),
        } as CSSProperties;
        return (
          <div
            key={index}
            className={`home-card${face ? " home-card-up" : ""}${initiallyUp[i] ? " home-card-still" : ""}`}
            style={style}
          >
            <div className={fidgetClass(fidget, i)} onAnimationEnd={done}>
              <div className="home-card-back">
                {back ? (
                  <img src={back} alt="" draggable={false} />
                ) : (
                  <div className="home-card-plain" style={{ background: theme.primary, color: theme.secondary }} />
                )}
              </div>
              <div className="home-card-face">{face && <img src={face} alt="" draggable={false} />}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
